import { inject, APP_INITIALIZER, Provider } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PermissionService } from '../services/permission.service';
import { AuthService } from '../services/auth.service';

/**
 * Factory que carga los permisos al iniciar la aplicación.
 * Espera a que AuthService.init() haya hidratado el signal isAuthenticated
 * ANTES de disparar GET /auth/permissions. Sin este await, el
 * cookieInterceptor (cookie.interceptor.ts:69-71) hace early-return cuando
 * isAuthenticated() todavía es false y la request sale sin la cookie
 * .hta_tenant_session → backend responde 403 TENANT_REQUIRED
 * (TenantResolutionMiddleware.cs:79). AuthService.init() es idempotente,
 * así que este await comparte la misma promesa que ProvideAuthProvider.
 */
function permissionInitializerFactory() {
  const permissionService = inject(PermissionService);
  const authService = inject(AuthService);

  return async () => {
    try {
      await authService.init();
      await firstValueFrom(permissionService.loadPermissions());
    } catch {
      // 401/403 = sin sesión o sin tenant aún: la app carga sin permisos
      // y el permissionGuard los recarga al resolver la primera ruta protegida.
    }
  };
}

/**
 * Provider para cargar permisos automáticamente al iniciar la app.
 * 
 * @example
 * ```typescript
 * // En app.config.ts
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     providePermissionInitializer(),
 *     // ... otros providers
 *   ]
 * };
 * ```
 */
export function providePermissionInitializer(): Provider {
  return {
    provide: APP_INITIALIZER,
    useFactory: permissionInitializerFactory,
    multi: true,
  };
}
